import { Button } from "rizzui";
import FormGroup from "@/app/shared/form-group";
import UploadZone from "../ui/file-upload/upload-zone";
import { RiLoaderFill } from "react-icons/ri";
import { useFormContext } from "react-hook-form";
import {
  IUpdateAssetXeroPayment,
  IXeroPayment,
} from "@/utilities/types/Payment";
import useApi from "@/hooks/useApi";
import { IResponseData, IUpdateResult } from "@/utilities/types/requests";
import PaymentRepository from "@/utilities/repositories/Payment";
import { IoIosArrowRoundBack } from "react-icons/io";
import toast from "react-hot-toast";
import { useCallback, useEffect, useState } from "react";
import { useAtom } from "jotai";
import { isUploadingAtom } from "@/store/atoms/upload-atom";
import ImageApp from "../ui/image-app";
import { IMAGES } from "@/assets/images";
import { EXeroPaymentMethod, PaymentFlow } from "@/utilities/enums/Payment";
import { useLocation } from "react-router-dom";
import { WHATSAPP_US_LINK } from "@/config/constants";
import { FaRegHeart } from "react-icons/fa";
import { useModal } from "@/app/shared/modal-views/use-modal";

interface UploadScreenShotNotificationProps {
  onClose: () => void;
  onBack: (initMethod?: EXeroPaymentMethod) => void;
  onPayNowComplete: () => void;
  payment?: IXeroPayment | null;
}

const SentScreenShot = ({ onOk }: { onOk: () => void }) => {
  return (
    <div className="p-8 flex flex-col justify-center items-center gap-3 w-[350px]">
      <div className="w-full flex flex-row justify-center items-center mb-4">
        <FaRegHeart size={60} />
      </div>
      <h2 className="text-xl font-bold text-black-700 mb-2 w-full text-center">
        Thank you! We're currently verifying your payment!
      </h2>
      <p className="text-gray-700 text-sm text-center mb-2">
        You may proceed to the dashboard, access will be granted once your
        payment has been successfully verified.
      </p>
      <div className="w-full flex flex-row justify-between items-center">
        <Button
          onClick={() => window.open(WHATSAPP_US_LINK, "_blank")}
          className="text-sm"
        >
          WhatsApp Us for Immediate Approval
        </Button>
      </div>
      <Button onClick={onOk} className="text-sm">
        OK
      </Button>
    </div>
  );
};

const SentScreenShotAdCredit = ({ onOk }: { onOk: () => void }) => {
  return (
    <div className="p-8 flex flex-col justify-center items-center gap-3 w-[350px]">
      <div className="w-full flex flex-row justify-center items-center mb-4">
        <FaRegHeart size={60} />
      </div>
      <h2 className="text-xl font-bold text-black-700 mb-2 w-full text-center">
        Thank you!
      </h2>
      <p className="text-gray-700 text-sm text-center mb-2">
        Please be patient while our team allocates payment. You may WhatsApp us
        for immediate allocation if urgent.
      </p>
      <div className="w-full flex flex-row justify-between items-center">
        <Button
          onClick={() => window.open(WHATSAPP_US_LINK, "_blank")}
          className="text-sm"
        >
          WhatsApp Us for Immediate Approval
        </Button>
      </div>
      <Button onClick={onOk} className="text-sm">
        OK
      </Button>
    </div>
  );
};

export default function UploadScreenShotNotification(
  props: UploadScreenShotNotificationProps
) {
  const { payment, onClose, onBack, onPayNowComplete } = props;
  const { state } = useLocation();
  const [isUploading] = useAtom<boolean>(isUploadingAtom);
  const [isFakeLoading, setIsFakeLoading] = useState<boolean>(true);
  const { openModal, closeModal } = useModal();

  const isAdCredit = state?.from === PaymentFlow.AD_CREDIT;

  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const {
    request: updateAssetXeroPayment,
    loading: isLoadingUpdateAssetXeroPayment,
    response: responseUpdateAssetXeroPayment,
  } = useApi<IResponseData<IUpdateResult>>({
    request: PaymentRepository.updateAssetXeroPayment,
  });

  const onSentAsset = () => {
    openModal({
      view: isAdCredit ? (
        <SentScreenShotAdCredit onOk={onClose} />
      ) : (
        <SentScreenShot onOk={onClose} />
      ),
      customSize: "350px",
    });
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const submitAssetXeroPayment = useCallback(async () => {
    const values = getValues();
    const asset = values?.asset?.at(0);

    if (!asset) {
      return toast.error("Please upload a screenshot before submitting!");
    }

    if (asset?.id && payment) {
      const data: IUpdateAssetXeroPayment = {
        assetId: asset.id,
        xeroPaymentId: payment.id,
      };

      try {
        await updateAssetXeroPayment(data);
        setValue("asset", []); // Reset asset value after successful submission
        onSentAsset();
        onPayNowComplete();
      } catch (error) {
        console.error("Error updating asset Xero payment:", error);
      }
    }
  }, [getValues, onPayNowComplete, payment, setValue, updateAssetXeroPayment]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFakeLoading(false);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="p-8 flex flex-col gap-3">
      {isFakeLoading && (
        <div className="flex flex-row items-center justify-center transparent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <RiLoaderFill className="animate-spin-slow" size={50} />
        </div>
      )}

      <div className={`${isFakeLoading ? "invisible" : ""}`}>
        <div>
          <div className={`flex justify-center`}>
            <ImageApp
              inApp
              src={IMAGES.QR_PAY_NOW}
              alt="qr_code_payment"
              width={330}
            />
          </div>
          <div className="mt-4">
            <p>1. Take a screenshot of the above PayNow QR</p>
          </div>
          <div>
            <p>2. Open your banking app & upload the above image</p>
          </div>
          <div>
            <p>3. Complete the payment and return back to this page</p>
          </div>
        </div>
        <div>
          <FormGroup
            description="Accept all type of images"
            title="Upload Screenshot"
          >
            <UploadZone
              name="asset"
              className="col-span-full upload-zone"
              getValues={getValues}
              setValue={setValue}
              error={errors?.desktopLogo?.message as string}
              inputProps={{
                multiple: false,
                accept: "image/*",
              }}
              autoUpload={true}
            />
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => onBack(EXeroPaymentMethod.PAY_NOW)}
                disabled={isLoadingUpdateAssetXeroPayment || isUploading}
              >
                <IoIosArrowRoundBack />
                Back
              </Button>
              <Button
                isLoading={isLoadingUpdateAssetXeroPayment}
                onClick={submitAssetXeroPayment}
                disabled={isLoadingUpdateAssetXeroPayment || isUploading}
              >
                Submit
              </Button>
            </div>
          </FormGroup>
        </div>
      </div>
    </div>
  );
}
